import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { PlusIcon } from '@heroicons/react/24/outline';
import api from '../../services/api.ts';
import { formatCurrency } from '../../utils/currency.ts';
import { useAuth } from '../../contexts/AuthContext.tsx';
import PageHeader from '../../components/ui/PageHeader.tsx';
import StatusChip from '../../components/ui/StatusChip.tsx';
import StackCell from '../../components/ui/StackCell.tsx';
import ConfirmationModal from '../../components/ConfirmationModal.tsx';
import { formatDateOnly } from '../../utils/date.ts';

interface Loan {
  id: string;
  description: string;
  creditor: string;
  type: string;
  amount: number;
  remainingAmount: number;
  interestRate: number;
  dueDate: string;
  status: string;
  installments?: number;
  currentInstallment?: number;
  details?: any;
}

const typeLabel: Record<string, string> = {
  loan: 'Empréstimo',
  financing: 'Financiamento',
  real_estate: 'Financiamento',
  vehicle: 'Financiamento',
  consortium: 'Consórcio/CDC',
};

const Dividas: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [search, setSearch] = useState('');

  const { data: items = [], isLoading, error } = useQuery<Loan[]>(
    'financeiro-dividas',
    async () => {
      const res = await api.get('/financeiro/dividas');
      return res.data;
    }
  );

  const filtered = useMemo(() => {
    return items.filter(item => {
      const matchesSearch =
        item.description.toLowerCase().includes(search.toLowerCase()) ||
        (item.creditor || '').toLowerCase().includes(search.toLowerCase());
      const matchesType =
        typeFilter === 'all' ||
        (typeFilter === 'loan' && item.type === 'loan') ||
        (typeFilter === 'financing' && ['financing', 'real_estate', 'vehicle'].includes(item.type));
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && item.status !== 'paid') ||
        (statusFilter === 'paid' && item.status === 'paid');
      return matchesSearch && matchesType && matchesStatus;
    });
  }, [items, search, typeFilter, statusFilter]);

  const active = items.filter(i => i.status !== 'paid');
  const saldoTotal = active.reduce((s, i) => s + Number(i.remainingAmount || 0), 0);
  const proximasParcelas = active.reduce((s, i) => {
    const monthly = Number((i as any).details?.monthlyPayment);
    if (monthly) return s + monthly;
    if (i.installments && i.installments > 0) {
      return (
        s +
        Number(i.remainingAmount) /
          Math.max(1, i.installments - (i.currentInstallment || 1) + 1)
      );
    }
    return s;
  }, 0);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500" />
      </div>
    );
  }
  if (error) return <div className="field-error">Erro ao carregar dívidas.</div>;

  return (
    <div>
      <PageHeader
        breadcrumb="Financeiro / Dívidas"
        title="Dívidas"
        subtitle="Empréstimos e financiamentos · módulo separado de Contas"
        actions={
          hasPermission('debts.create') ? (
            <button
              type="button"
              className="btn btn-primary btn-block-mobile"
              onClick={() => navigate('/financeiro/dividas/nova')}
            >
              <PlusIcon style={{ width: 18, height: 18 }} />
              Nova dívida
            </button>
          ) : undefined
        }
      />

      <div className="toolbar">
        <div className="filters">
          <label className="filter-field">
            <span className="filter-label">Tipo</span>
            <select className="select" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
              <option value="all">Todos</option>
              <option value="loan">Empréstimo</option>
              <option value="financing">Financiamento</option>
            </select>
          </label>
          <label className="filter-field">
            <span className="filter-label">Status</span>
            <select className="select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="active">Em aberto</option>
              <option value="paid">Quitadas</option>
              <option value="all">Todos</option>
            </select>
          </label>
          <label className="filter-field" style={{ flex: 1 }}>
            <span className="filter-label">Buscar</span>
            <input
              className="input search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Credor ou descrição"
            />
          </label>
        </div>
      </div>

      <div className="kpi-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div className="kpi">
          <div className="kpi-label">Saldo em dívidas</div>
          <div className="kpi-value negative">{formatCurrency(saldoTotal)}</div>
          <div className="kpi-hint">{active.length} ativas</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Próximas parcelas (est.)</div>
          <div className="kpi-value">{formatCurrency(proximasParcelas)}</div>
          <div className="kpi-hint">estimativa mensal</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Contratos</div>
          <div className="kpi-value">{items.length}</div>
          <div className="kpi-hint">total cadastrado</div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">Contratos</div>
          <span className="muted">{filtered.length} registros</span>
        </div>
        {filtered.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center' }} className="muted">
            Nenhuma dívida encontrada.
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data-table compact-mobile">
              <thead>
                <tr>
                  <th>Descrição</th>
                  <th className="hide-m">Tipo</th>
                  <th className="hide-m">Vencimento</th>
                  <th className="hide-m">Status</th>
                  <th className="hide-m" style={{ textAlign: 'right' }}>Saldo</th>
                  <th style={{ textAlign: 'right' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(item => {
                  const paid = item.status === 'paid';
                  return (
                    <tr key={item.id}>
                      <td>
                        <StackCell
                          title={item.description}
                          meta={item.creditor || undefined}
                          stackExtra={
                            <>
                              {typeLabel[item.type] || item.type} · {formatDateOnly(item.dueDate)} ·{' '}
                              {paid ? 'Quitada' : 'Em aberto'} · {formatCurrency(item.remainingAmount)}
                            </>
                          }
                        />
                      </td>
                      <td className="hide-m">{typeLabel[item.type] || item.type}</td>
                      <td className="mono hide-m">
                        {formatDateOnly(item.dueDate)}
                      </td>
                      <td className="hide-m">
                        <StatusChip
                          label={paid ? 'Quitada' : 'Em aberto'}
                          tone={paid ? 'green' : 'amber'}
                        />
                      </td>
                      <td className="amount hide-m" style={{ textAlign: 'right' }}>
                        {formatCurrency(item.remainingAmount)}
                      </td>
                      <td>
                        <div className="row-actions">
                          <Link className="link-btn action-open" to={`/debts/${item.id}`}>
                            Abrir
                          </Link>
                          {hasPermission('debts.delete') && (
                            <button
                              type="button"
                              className="link-btn action-keep action-danger"
                              onClick={() => { setDeleteId(item.id); setShowDeleteModal(true); }}
                            >
                              Excluir
                            </button>
                          )}
                          {!paid && hasPermission('debts.update') && (
                            <Link className="link-btn action-secondary" to={`/financeiro/dividas/${item.id}/pagar`}>
                              Pagar
                            </Link>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setDeleteId(null); }}
        title="Excluir dívida"
        message="Tem certeza que deseja excluir esta dívida? Esta ação não pode ser desfeita."
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
        isLoading={deleting}
        onConfirm={async () => {
          if (!deleteId) return;
          setDeleting(true);
          try {
            await api.delete(`/debts/${deleteId}`);
            queryClient.invalidateQueries('financeiro-dividas');
          } finally {
            setDeleting(false);
            setShowDeleteModal(false);
            setDeleteId(null);
          }
        }}
      />
    </div>
  );
};

export default Dividas;
